import type { AudioCodecSpecificData } from '../types/audioCodecSpecificData';
import { invariant } from '../util/invariant';
import type { Mp4Info } from './mp4Types';
import { extractH264FrameInfoFromAvcCBox, getCodecString, getH264FrameInfo, getMp4ProgressiveStatus, type AudioSpecificConfig, type Avc1VisualSampleEntry, type AvcC, type EditListBox, type ElementaryStreamDescriptorBox, type HandlerBox, type MovieHeaderBox, type Mp4aBox, type Mp4Box, type SampleDescriptionBox, type TrackHeaderBox } from './parser';
import { populateVirtualSegments } from './virtualSegments';

export function inspectMoov(
  mp4Info: Mp4Info | undefined,
  moovBox: Mp4Box,
  virtualSegmentUrl: string | undefined,
  ignoreVideo: boolean,
  ignoreAudio: boolean,
): Mp4Info {
  const videoTracks = mp4Info ? mp4Info.videoTracks : [];
  const audioTracks = mp4Info ? mp4Info.audioTracks : [];
  let virtualSegments = mp4Info ? mp4Info.virtualSegments : null;

  const nExistingAudioTracks = audioTracks.length;

  let videoStblBoxes: Mp4Box[] = [];
  let audioStblBoxes: Mp4Box[] = [];

  moovBox.children.forEach(box => {
    if (box.type === 'trak') {
      const tkhdBox = box.children.find(b => b.type === 'tkhd') as TrackHeaderBox | undefined;
      invariant(tkhdBox, 'Could not find tkhd box');
      const trackId = tkhdBox.track_id;

      let mediaStartPtsOffset = 0;
      const edtsBox = box.children.find(b => b.type === 'edts') as Mp4Box | undefined;
      if (edtsBox !== undefined) {
        const elstBox = edtsBox.children.find(b => b.type === 'elst') as EditListBox | undefined;
        if (elstBox !== undefined) {
          if (elstBox.entries.length > 0) {
            mediaStartPtsOffset = elstBox.entries[0]!.media_time;
          }
        }
      }

      const mdiaBox = box.children.find(b => b.type === 'mdia');
      invariant(mdiaBox, 'Could not find mdia box');

      const mdhdBox = mdiaBox.children.find(b => b.type === 'mdhd') as MovieHeaderBox | undefined;
      invariant(mdhdBox, 'Could not find mdhd box');
      const timescale = mdhdBox.timescale;

      const hdlrBox = mdiaBox.children.find(b => b.type === 'hdlr') as HandlerBox | undefined;
      invariant(hdlrBox, 'Could not find hdlr box');

      const minfBox = mdiaBox.children.find(b => b.type === 'minf') as Mp4Box | undefined;
      invariant(minfBox, 'Could not find minf box');

      const stblBox = minfBox.children.find(b => b.type === 'stbl') as Mp4Box | undefined;
      invariant(stblBox, 'Could not find stbl box');

      const stsdBox = stblBox.children.find(b => b.type === 'stsd') as SampleDescriptionBox | undefined;
      invariant(stsdBox, 'Could not find stsd box');

      if (hdlrBox.handler_type === 'vide' && !ignoreVideo) {
        // Video
        const avc1Box = stsdBox.children.find(b => b.type === 'avc1') as Avc1VisualSampleEntry | undefined;
        invariant(avc1Box, 'Could not find avc1 box');

        const avcCBox = avc1Box.children.find(b => b.type === 'avcC') as AvcC | undefined;
        invariant(avcCBox, 'Could not find avcC box');
        const avcC = new Uint8Array(avcCBox.avcC);
        const width = avc1Box.width;
        const height = avc1Box.height;

        const h264FrameInfo = extractH264FrameInfoFromAvcCBox(avcCBox);

        videoTracks.push({
          codecString: getCodecString(avcCBox),
          progressiveStatus: getMp4ProgressiveStatus(h264FrameInfo),
          pixelAspectRatio: (h264FrameInfo !== null) ? h264FrameInfo.sampleAspectRatio : undefined,
          trackId,
          timescale,
          avcC,
          width,
          height,
          frameDuration: 0,
          mediaStartPtsOffset,
          nalLengthSize: avcCBox.lengthSizeMinusOne + 1,
          frameInfo: getH264FrameInfo(avcCBox),
        });
        videoStblBoxes.push(stblBox);
      } else if (hdlrBox.handler_type  === 'soun' && !ignoreAudio) {
        const mp4aBox = stsdBox.children.find(b => b.type === 'mp4a') as Mp4aBox | undefined;
        invariant(mp4aBox, 'Could not find mp4a box');

        let sampleRate = mp4aBox.samplerate;
        const bitsPerSample = mp4aBox.samplesize;

        let codecSpecificData : AudioCodecSpecificData | null = null;

        // We have observed that the channel count in the mp4a box is sometimes
        // incorrect. To avoid issues when this is the case, we try to extract
        // the codec specific information from the esds box
        let channelCount = mp4aBox.channelcount;
        const esdsBox = mp4aBox.children.find(b => b.type === 'esds') as ElementaryStreamDescriptorBox | undefined;
        if (esdsBox) {
          const audioSpecificConfig = esdsBox.es.decConfigDescr.decSpecificInfo.find(info => typeof (info as any).audioObjectType === 'number') as AudioSpecificConfig | undefined;
          if (audioSpecificConfig) {
            switch(audioSpecificConfig.channelConfiguration) {
              case 1:
              case 2:
              case 3:
              case 4:
              case 5:
              case 6: channelCount = audioSpecificConfig.channelConfiguration; break;
              case 7: channelCount = 8; break;
              case 11: channelCount = 7; break;
              case 12: channelCount = 8; break;
              case 14: channelCount = 8; break;
              default: break;
            }
            codecSpecificData = {
              type: 'aac',
              audioObjectType: audioSpecificConfig.audioObjectType,
              samplingFrequencyIndex: audioSpecificConfig.samplingFrequencyIndex,
              channelConfiguration: audioSpecificConfig.channelConfiguration,
            };
          }
        }

        // For sample rate values that cannot be represented in & 16 bit integer,
        // it is expected that the samplerate property is set to 0. In such a case,
        // the sample rate should be extracted from the Elementary Stream Descriptor.
        // However, in practice it is often the case that the timescale has the correct
        // value, at least for files generated by ffmpeg. So as a quick fix, we try to
        // default to that if we find a samplerate of 0 with the timescale being one of
        // the valid AAC sample rates that are too large for 16 bits
        if (sampleRate === 0) {
          if (timescale === 88200 || timescale === 96000 || timescale === 176400 || timescale === 192000) {
            sampleRate = timescale;
          }
        }

        // Audio
        audioTracks.push({
          trackId,
          timescale,
          sampleRate,
          bitsPerSample,
          channelCount,
          frameDuration: 0,
          mediaStartPtsOffset,
          codecSpecificData,
        });
        audioStblBoxes.push(stblBox);
      }
    }
  });

  if (virtualSegmentUrl !== undefined) {
    // If we have video, we want to create the virtual segments after the GOPs
    invariant(videoTracks.length <= 1, 'Cannot handle more than 1 video stream');
    invariant(videoStblBoxes.length <= 1, 'Cannot handle more than 1 video stream inside file');
    if (videoStblBoxes.length === 1) {
      virtualSegments = populateVirtualSegments(virtualSegmentUrl, virtualSegments, null, videoTracks[0]!.mediaStartPtsOffset, videoTracks[0]!.timescale, videoStblBoxes[0]!);
      if (virtualSegments !== null && virtualSegments.length > 0 && virtualSegments[0]!.videoData!.samples.length > 0) {
        videoTracks[0]!.frameDuration = virtualSegments[0]!.videoData!.samples[0]!.duration;
      }
    }

    for (let i = nExistingAudioTracks; i < audioTracks.length; i++) {
      virtualSegments = populateVirtualSegments(virtualSegmentUrl, virtualSegments, i, audioTracks[i]!.mediaStartPtsOffset, audioTracks[i]!.timescale, audioStblBoxes[i - nExistingAudioTracks]!);
      if (virtualSegments !== null && virtualSegments.length > 0 && virtualSegments[0]!.audioData[i]!.samples.length > 0) {
        audioTracks[i]!.frameDuration = virtualSegments[0]!.audioData[i]!.samples[0]!.duration;
      }
    }
  }

  return {
    videoTracks,
    audioTracks,
    virtualSegments: virtualSegments ?? [],
  };
}
